import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Card, Form, Button, Spinner, Alert, Row, Col,
} from 'react-bootstrap';
import NotificationService from '../../Services/NotificationService';
import PhoneNotifications from './PhoneNotifications';

const NOTIFICATION_TYPES = [
  {
    key: 'booking_created',
    label: 'New Bookings',
    description: 'When a new booking is created',
  },
  {
    key: 'booking_updated',
    label: 'Booking Updates',
    description: 'When a booking is modified',
  },
  {
    key: 'operator_assigned',
    label: 'Operator Assignments',
    description: 'When an operator is assigned to a booking',
  },
  {
    key: 'partner_sync',
    label: 'Partner Sync',
    description: 'Updates from partner integrations',
  },
  {
    key: 'quality_assurance',
    label: 'Quality Assurance',
    description: 'Alerts about bookings without drivers or other quality issues',
    inAppForced: true, // In-app notifications cannot be disabled for this type
  },
  {
    key: 'trip_claim',
    label: 'Trip Claims',
    description: 'Receipts and overtime filed by drivers and operators, waiting for review',
  },
  {
    key: 'booking_reminder',
    label: 'Booking Reminders',
    description: 'Reminders you or a colleague set on a booking, when they fall due',
  },
  {
    key: 'daily_digest',
    label: 'Daily Digest',
    description: 'Daily summary of upcoming trips',
  },
  {
    key: 'system',
    label: 'System Notifications',
    description: 'System alerts and announcements',
  },
  {
    key: 'custom',
    label: 'Custom Notifications',
    description: 'Other notifications sent by team members',
  },
];

function NotificationSettings() {
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [myGroups, setMyGroups] = useState([]);

  const fetchPreferences = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [prefs, groups] = await Promise.all([
        NotificationService.getPreferences(),
        NotificationService.getMyGroups(),
      ]);
      setPreferences(prefs);
      setMyGroups(groups || []);
    } catch (err) {
      setError('Failed to load notification preferences');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleGlobalToggle = (field, value) => {
    setPreferences((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTypeToggle = (typeKey, channel, value) => {
    setPreferences((prev) => ({
      ...prev,
      TypePreferences: {
        ...prev.TypePreferences,
        [typeKey]: {
          ...prev.TypePreferences?.[typeKey],
          [channel]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      await NotificationService.updatePreferences({
        inAppEnabled: preferences.InAppEnabled,
        emailEnabled: preferences.EmailEnabled,
        typePreferences: preferences.TypePreferences,
      });

      setSuccessMessage('Preferences saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Container className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading preferences...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="fa fa-bell me-2" />
            Notification Settings
          </h2>
          <p className="text-muted mb-0">
            Manage how and when you receive notifications
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <i className="fa fa-exclamation-circle me-2" />
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          <i className="fa fa-check-circle me-2" />
          {successMessage}
        </Alert>
      )}

      {/* Global Settings */}
      <Card className="mb-4">
        <Card.Header className="bg-dark text-white">
          <i className="fa fa-sliders-h me-2" />
          Global Settings
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="inAppEnabled"
                label={(
                  <div>
                    <strong>In-App Notifications</strong>
                    <div className="text-muted small">
                      Show notifications in the dashboard
                    </div>
                  </div>
                )}
                checked={preferences?.InAppEnabled ?? true}
                onChange={(e) => handleGlobalToggle('InAppEnabled', e.target.checked)}
                className="mb-3"
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="emailEnabled"
                label={(
                  <div>
                    <strong>Email Notifications</strong>
                    <div className="text-muted small">
                      Receive notifications via email
                    </div>
                  </div>
                )}
                checked={preferences?.EmailEnabled ?? true}
                onChange={(e) => handleGlobalToggle('EmailEnabled', e.target.checked)}
                className="mb-3"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/*
        Phone notifications sit between the global switches and the per-type
        table on purpose: it is a DEVICE list, not a third channel. What gets
        pushed is decided by the In-App column below — a push goes exactly
        where a bell row goes — so the card reads as "and also on these
        phones", which is what it is.
      */}
      <PhoneNotifications />

      {/* Per-Type Settings */}
      <Card className="mb-4">
        <Card.Header className="bg-dark text-white">
          <i className="fa fa-list me-2" />
          Notification Types
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            Fine-tune which types of notifications you want to receive through each channel.
          </p>

          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '40%' }}>Notification Type</th>
                  <th className="text-center" style={{ width: '30%' }}>In-App</th>
                  <th className="text-center" style={{ width: '30%' }}>Email</th>
                </tr>
              </thead>
              <tbody>
                {NOTIFICATION_TYPES.map((type) => {
                  const typePrefs = preferences?.TypePreferences?.[type.key] || {};
                  const inAppEnabled = type.inAppForced ? true : (typePrefs.inApp ?? true);
                  const emailEnabled = typePrefs.email ?? true;

                  return (
                    <tr key={type.key}>
                      <td>
                        <strong>{type.label}</strong>
                        <div className="text-muted small">{type.description}</div>
                        {type.inAppForced && (
                          <div className="text-warning small">
                            <i className="fa fa-info-circle me-1" />
                            In-app notifications are always enabled for this type
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="switch"
                          id={`${type.key}-inApp`}
                          checked={inAppEnabled}
                          onChange={(e) => handleTypeToggle(type.key, 'inApp', e.target.checked)}
                          disabled={!preferences?.InAppEnabled || type.inAppForced}
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="switch"
                          id={`${type.key}-email`}
                          checked={emailEnabled}
                          onChange={(e) => handleTypeToggle(type.key, 'email', e.target.checked)}
                          disabled={!preferences?.EmailEnabled}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* My Groups */}
      {myGroups.length > 0 && (
        <Card className="mb-4">
          <Card.Header className="bg-dark text-white">
            <i className="fa fa-users me-2" />
            My Notification Groups
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              You are a member of the following notification groups:
            </p>
            <div className="d-flex flex-wrap gap-2">
              {myGroups.map((group) => (
                <span
                  key={group.GroupId}
                  className="badge bg-primary px-3 py-2"
                  style={{ fontSize: '0.9rem' }}
                >
                  <i className="fa fa-users me-2" />
                  {group.GroupName}
                </span>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Save Button */}
      <div className="d-flex justify-content-end">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="fa fa-save me-2" />
              Save Preferences
            </>
          )}
        </Button>
      </div>
    </Container>
  );
}

export default NotificationSettings;
